<script>
import BookingBreadcrumb from '@/components/booking/booking-breadcrumb.vue'
import BookingConfirmationsList from '@/components/booking/booking-confirmations-list.vue'
import BookingCreateRestrictedAlert from '@/components/booking/booking-create-restricted-alert.vue'
import BookingPassedCutOffCard from '@/components/booking/booking-past-cut-off-card.vue'
import PromiseDialogOld from '@/components/general/promise-dialog-old.vue'
import { isNullOrEmptyArray } from '@/helpers/array-helpers'
import baseMixin from '@/mixins/baseMixin'
import bookingMixin from '@/mixins/bookingMixin'
import dateMixin from '@/mixins/dateMixin'
import errorMixin from '@/mixins/errorMixin'
import ClientLoginLocationViewModel from '@/models/locations/clientLoginLocationViewModel'
import { BookingPayloadFactory } from '@/services/booking/BookingPayloadFactory.js'
import dayjs from '@/services/date'
import { BookingCommentType } from '@/shared/constants/booking/BookingCommentType'
import BookingType from '@/shared/constants/booking/BookingType'
import { ClientType } from '@/shared/constants/client/ClientType'
import { PhoneType } from '@/shared/constants/contact/PhoneType'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import calcTimeDifference from '@/utils/calc-time-difference'
import { isEqual } from 'lodash'
import { nanoid } from 'nanoid'
import { mapActions, mapGetters } from 'vuex'
import EmptyStateBlock from '../general/empty-state-block.vue'
import ErrorAlert from '../general/error-alert.vue'
import BookingConfirmationListSkeleton from '../skeleton/booking-confirmation-list-skeleton.vue'
import BookingCreateForm from './booking-create-form.vue'
import DateAlertBanner from './date-alert-banner.vue'
import EditBanner from './edit-banner.vue'

const { EARLY_CHILDHOOD, OSHC, KINDERGARTEN } = ClientType

export default {
  name: 'BookingCreateMeta',
  components: {
    BookingPassedCutOffCard,
    BookingConfirmationsList,
    BookingCreateForm,
    BookingConfirmationListSkeleton,
    PromiseDialogOld,
    ErrorAlert,
    BookingCreateRestrictedAlert,
    EmptyStateBlock,
    BookingBreadcrumb,
    EditBanner,
    DateAlertBanner,
  },
  mixins: [baseMixin, errorMixin, dateMixin, bookingMixin],
  props: {
    editMode: { type: Boolean, default: false },
    bookingMode: { type: String, default: null },
    booking: { type: Object, default: null },
    selectedLocation: {
      type: ClientLoginLocationViewModel,
      required: true,
    },
    preselectDate: {
      type: String,
      default: '',
    },
  },
  data: () => {
    return {
      payload: null,
      originalSnapshot: null,
      snapshotConfirmations: null,
      formChanged: false,
      showPastCurrentDayDialog: false,
      isBookingTimeRangeValid: true,
      multipleDaysCheckbox: false,
      numberOfBookingsPerDate: 1,
      earliestEndTime: null,
      minDurationMinutes: 0,
      dates: [],
      minDate: '',
      hideNotifyMeCheckbox: false,
      numberOfPhoneNumberProps: 3,
      isInit: false,
      minConfirmationContacts: 1,
      mandatoryCertIds: [],
      ignoreSameDayCutOffTypes: [EARLY_CHILDHOOD, OSHC, KINDERGARTEN],
      BookingType,
    }
  },
  computed: {
    ...mapGetters({
      locale: 'locale',
      bookingContacts: 'contacts/bookingContacts',
      isLoadingBookingContacts: 'contacts/isLoadingBookingContacts',
      isLoadingClassifications: 'client/isLoadingClassifications',
      currentUser: 'auth/currentUser',
      isLoadingCRUD: 'bookings/isLoadingCRUD',
      isLoadingEditBooking: 'bookings/isLoadingEditBooking',
      isLoadingBookingBlocks: 'locations/isLoadingBookingBlocks',
      getClassifications: 'client/classifications',
      getRestrictionDetailsByLocationId:
        'locations/getRestrictionDetailsByLocationId',
    }),
    hasBookingRestriction() {
      return !!this.getRestrictionDetailsByLocationId(
        this.selectedLocation.locationId
      )
    },
    excludedDates() {
      if (!this.hasBookingRestriction) return null
      const restriction = this.getRestrictionDetailsByLocationId(
        this.selectedLocation.locationId
      )

      const formattedExcludedDates = []

      for (const date of restriction.datesLocal) {
        formattedExcludedDates.push(dayjs(date).format('YYYY-MM-DD'))
      }

      return formattedExcludedDates
    },
    timezone() {
      return this.selectedLocation.timeZone
    },
    isValid() {
      return (
        this.dates.length > 0 &&
        this.isBookingTimeRangeValid &&
        !this.hasExceededMaxAllowedBookings &&
        !this.isBelowMinBookingDuration &&
        !this.isBeforeMinEndTime &&
        this.hasFilledAllRequiredFields &&
        this.hasMinNumberOfConformationContacts
      )
    },
    contacts() {
      return this.bookingContacts(this.selectedLocation.locationId)
    },
    confirmations() {
      return this.payload.templates[0].confirmations
    },
    hasFilledAllRequiredFields() {
      return !!this.payload.templates[0].classification
    },
    getLocale() {
      return this.$i18n.locale
    },
    hasExceededMaxAllowedBookings() {
      if (!this.dates || this.dates.length === 0) return false
      return (
        this.dates.length * this.numberOfBookingsPerDate >
        this.maxNumBookingsPerDay
      )
    },
    hasMinNumberOfConformationContacts() {
      return this.confirmations?.length >= this.minConfirmationContacts
    },
    isBelowMinBookingDuration() {
      return this.payload.templates[0].durationMinutes < this.minDurationMinutes
    },
    isBeforeMinEndTime() {
      return this.payload.templates[0].endTimeLocation < this.earliestEndTime
    },
    compSubmitButtonErrorTooltip() {
      if (this.hasExceededMaxAllowedBookings)
        return this.$t('bookingCreate.validationMessageExceededMaxBookings')
      if (this.dates.length === 0)
        return this.$t('bookingCreate.validationMessageNoDatesSelected')
      if (this.isBelowMinBookingDuration)
        return this.$t('bookingCreate.validationMessageBelowMinDuration', {
          minDurationMinutes: this.minDurationMinutes / 60,
        })
      if (this.isBeforeMinEndTime)
        return this.$t('bookingCreate.validationMessageBeforeEarliestEndTime', {
          earliestEndTime: this.earliestEndTime,
        })
      if (!this.isBookingTimeRangeValid)
        return this.$t('bookingCreate.validationMessageInvalidTimeRange')
      if (!this.hasFilledAllRequiredFields)
        return this.$t('bookingCreate.validationMessageMissingRequiredFields')
      if (!this.hasMinNumberOfConformationContacts)
        return this.$t('bookingCreate.validationMessageBelowMinConfContacts')
      return ''
    },
    hasClassifications() {
      return !isNullOrEmptyArray(
        this.getClassifications(this.selectedLocation?.clientId)
      )
    },
    showNoClassificationsError() {
      if (this.isClassificationHidden) return false
      return !this.hasClassifications && !this.isLoadingClassifications
    },
    isClassificationHidden() {
      if (!this.editMode || !this.booking) return false
      return (
        this.booking.details &&
        this.booking.details.payClass &&
        this.booking.details.payClass.isHidden === true
      )
    },
    isBlockBooking() {
      return this.bookingMode === BookingType.Block
    },
    isSingleDayBooking() {
      return this.bookingMode === BookingType.SingleDay
    },
    showNoDatesAlert() {
      return (
        !this.editMode &&
        this.bookingMode &&
        this.booking &&
        this.dates.length <= 0
      )
    },
    editDescription() {
      if (!this.editMode || !this.booking) return ''

      const { groupId } = this.booking

      switch (this.bookingMode) {
        case BookingType.Block:
          return this.$t('bookingEdit.editBanner.editBlockDescription')
        case BookingType.SingleDay:
          return groupId !== null
            ? this.$t('bookingEdit.editBanner.editSingleDayInBlockDescription')
            : this.$t('bookingEdit.editBanner.editSingleDayDescription')
        default:
          return ''
      }
    },
    isDatePickerDisabled() {
      return (
        this.editMode &&
        this.isSingleDayBooking &&
        this.booking?.groupId !== null
      )
    },
  },
  created() {
    this.minDate = dayjs()
      .tz(this.timezone)
      .format('YYYY-MM-DD')
    this.initPayload()

    if (this.preselectDate) this.dates = [this.preselectDate]

    // Prefill booking, if booking is found
    if (this.bookingMode && this.booking) {
      this.prefillBooking()
    }
  },
  methods: {
    ...mapActions({
      loadClientClassifications: 'client/loadClientClassifications',
      loadClientGrades: 'client/loadClientGrades',
      loadBookingContacts: 'contacts/loadBookingContacts',
      loadLocationCertifications: 'locations/loadLocationCertifications',
      submitBooking: 'bookings/submitBooking',
      editBooking: 'bookings/editBooking',
      loadClientAdditionalInformation: 'client/loadClientAdditionalInformation',
      loadLocationBookingBlocks: 'locations/loadLocationBookingBlocks',
    }),
    prefillBooking() {
      if (!this.booking) return

      const details = this.booking.details

      this.payload.templates[0].classification = details.payClass?.id || null

      // Prefill dates for Block
      if (this.isBlockBooking) {
        if (this.booking.dates && this.booking.dates.length > 0) {
          if (this.editMode) {
            this.dates = this.booking.dates.map((bookingDate) =>
              dayjs(bookingDate.date).format(DateFormatToken.YYYYMMDD)
            )
          } else {
            this.dates = this.booking.dates
              .filter((dateObj) => dayjs(dateObj.date).isAfter(dayjs()))
              .map((dateObj) =>
                dayjs(dateObj.date).format(DateFormatToken.YYYYMMDD)
              )
          }
        } else if (details.earliestDate) {
          this.dates = [details.earliestDate]
        }
      }
      // copy the booking
      this.captureOriginalSnapshot()
    },
    prefillConfirmationList() {
      if (!this.booking) return

      const details = this.booking.details

      if (
        this.booking.clientLocation.locationId ===
        this.selectedLocation.locationId
      ) {
        this.$set(this.payload.templates[0], 'confirmations', [])
        if (details.confirmations?.length) {
          this.payload.templates[0].confirmations = details.confirmations.map(
            (confirmationContact) => {
              if (confirmationContact.id === this.currentUser.id) {
                this.hideNotifyMeCheckbox = true
              }

              return {
                contactId: confirmationContact.id,
                fullName: confirmationContact.fullName || '',
                email: !!confirmationContact.notificationDetails?.email,
                sms: confirmationContact.notificationDetails?.sms || null,
                voice: confirmationContact.notificationDetails?.voice || null,
              }
            }
          )
        }
        this.updated()
      } else {
        this.initConfirmations(this.snapshotConfirmations)
      }
    },
    captureOriginalSnapshot() {
      const bookingDetail = this.booking.details
      const groupId = this.booking.groupId
      let bookingDates = [bookingDetail.earliestDate]
      if (this.isBlockBooking) {
        bookingDates = this.booking.dates.map((bookingDate) =>
          dayjs(bookingDate.date).format(DateFormatToken.YYYYMMDD)
        )
      } else {
        if (groupId !== null) {
          bookingDates = this.booking.dates
            .filter((bookingDate) => bookingDate.id === this.booking.id)
            .map((bookingDate) =>
              dayjs(bookingDate.date).format(DateFormatToken.YYYYMMDD)
            )
        }
      }
      let multipleBooking = false
      let confirmList = null
      if (bookingDetail.confirmations?.length) {
        confirmList = bookingDetail.confirmations.map((confirmationContact) => {
          return {
            contactId: confirmationContact.id,
            fullName: confirmationContact.fullName || '',
            email: !!confirmationContact.notificationDetails?.email,
            sms: confirmationContact.notificationDetails?.sms || null,
            voice: confirmationContact.notificationDetails?.voice || null,
          }
        })
      }

      if (this.isBlockBooking && groupId !== null) {
        multipleBooking = true
      }

      this.originalSnapshot = {
        numberOfBookingsPerDate: 1,
        multipleBooking: multipleBooking,
        dates: bookingDates,
        startTime: bookingDetail.startTime,
        endTime: bookingDetail.endTime,
        classificationId: bookingDetail.payClass?.id || null,
        minutesBreak: this.booking.minutesBreak,
        grades: bookingDetail.grades.map((grade) => grade.id),
        submittedByOrReplacing: bookingDetail.covering?.id || null,
        room: bookingDetail.room || '',
        reason: bookingDetail.covering?.reason || '',
        notes: bookingDetail.notes || '',
        purchaseOrderNumber: bookingDetail.purchaseOrderNumber || '',
        certifications: bookingDetail.certifications.map((cert) => cert.id),
        confirmations: confirmList,
        locationId: this.booking.clientLocation.locationId,
      }
    },
    ignoreSameDayCutOff() {
      /**
       * This bypass was added due to a request from Darragh Kelly/Craig Griffiths.
       * They requested that Australian Early Childhood/OSHC clients to be exempted from our self-serve booking creation 8am cut off.
       * @see {@link https://dev.azure.com/anzukeducation/Staffroom%20NEXT User Story #2298}
       */
      return (
        this.ignoreSameDayCutOffTypes.includes(
          this.selectedLocation.clientTypeInt
        ) && this.selectedLocation.country === 'Australia'
      )
    },
    allowedDates(val) {
      // Don't exclude dates if restriction not applied
      if (!this.excludedDates || this.excludedDates?.length === 0) return true

      const dateInYYYYMMDD = dayjs(val).format('YYYY-MM-DD')
      return !this.excludedDates.includes(dateInYYYYMMDD)
    },
    initRoom(contactList) {
      if (this.selectedLocation.submittedByTemplateRequired) return
      const note = this.payload.templates[0].notes.find(
        (x) => x.type === BookingCommentType.CONTACT
      )
      const currentContact = contactList.find(
        (x) => x.id === this.currentUser.id
      )
      note.content = currentContact.room
    },
    initConfirmations(contactList) {
      // Init confirmations
      const contactsWithNotificationDetails = contactList.filter(
        (contact) => contact.notificationDetails
      )

      if (contactsWithNotificationDetails.length === 0) return

      for (const contact of contactsWithNotificationDetails) {
        if (contact.id === this.currentUser.id) this.hideNotifyMeCheckbox = true

        this.payload.templates[0].confirmations.push({
          contactId: contact.id,
          fullName: contact.fullName,
          email: contact.notificationDetails.email,
          sms: contact.notificationDetails.sms,
          voice: contact.notificationDetails.voice,
        })
      }
    },
    initPayload() {
      // Generate payload
      this.payload = BookingPayloadFactory(
        this.selectedLocation,
        this.currentUser.id
      )

      this.loadBookingContacts(this.selectedLocation.locationId).then(
        (result) => {
          if (!result.isSuccess || result.data.length === 0) return
          this.snapshotConfirmations = result.data
          // Init confirmations
          if (!this.editMode && !this.booking) {
            this.initConfirmations(result.data)
          } else {
            // Prefill Confirmation List after location fetched
            this.prefillConfirmationList()
          }

          // Preset the room number
          this.initRoom(result.data)
        }
      )

      this.loadClientAdditionalInformation(this.selectedLocation.clientId)
      this.loadClientGrades(this.selectedLocation.clientId)
      this.loadLocationCertifications(this.selectedLocation.locationId).then(
        (result) => {
          if (!result.isSuccess || result.data.length === 0) return

          const mandatoryIds = result.data
            .filter((cert) => cert.selected)
            .map((cert) => cert.id)

          const existingCerts = this.payload.templates[0].certifications || []
          const merged = [...new Set([...mandatoryIds, ...existingCerts])]

          this.$set(this.payload.templates[0], 'certifications', merged)

          this.mandatoryCertIds = [...mandatoryIds]
        }
      )

      if (!this.isClassificationHidden) {
        this.loadClientClassifications(this.selectedLocation.clientId)
      }
    },
    updateTime(payload) {
      if (!payload.startTime || !payload.endTime)
        return (this.isBookingTimeRangeValid = false)

      this.payload.templates[0].startTimeLocation = payload.startTime
      this.payload.templates[0].endTimeLocation = payload.endTime

      this.payload.templates[0].durationMinutes = calcTimeDifference(
        payload.startTime,
        payload.endTime
      )

      this.isBookingTimeRangeValid = payload.isValid
      this.updated()
    },
    updateGrades(value) {
      this.payload.templates[0].grades = value
      this.updated()
    },
    updateCerts(value) {
      this.payload.templates[0].certifications = value
      this.updated()
    },
    setBookingParametersOnClassificationChange(value) {
      if (!this.hasClassifications) return

      const classifications = this.getClassifications(
        this.selectedLocation?.clientId
      )

      const selectedClassification = classifications.find(
        (classification) => classification.id === value
      )

      if (!selectedClassification) return

      this.minDurationMinutes =
        selectedClassification?.extraValidation?.minimumDurationInMins
      this.earliestEndTime = selectedClassification.earliestEndTime
    },
    updateClassification(value) {
      this.payload.templates[0].classification = value
      this.setBookingParametersOnClassificationChange(value)
      this.updated()
    },
    updateReason(value) {
      this.payload.templates[0].reason = value
      this.updated()
    },
    updateRoom(value) {
      const note = this.payload.templates[0].notes.find(
        (x) => x.type === BookingCommentType.CONTACT
      )
      note.content = value
      this.updated()
    },
    updateSubmittedBy(value) {
      this.payload.templates[0].submittedByOrReplacing = value
      this.updated()
    },
    updateNotes(value) {
      this.payload.templates[0].notes.find(
        (x) => x.type === BookingCommentType.CANDIDATE
      ).content = value
      this.payload.templates[0].notes.find(
        (x) => x.type === BookingCommentType.PRIVATE
      ).content = value
      this.updated()
    },
    updatePurchaseOrderNumber(value) {
      this.payload.templates[0].purchaseOrderNumber = value
      this.updated()
    },
    updateBreaks(value) {
      this.payload.templates[0].breakMinutes = value
      this.updated()
    },
    updated() {
      if (this.editMode) {
        const bookingSnapshot = this.originalSnapshot
        const template = this.payload.templates[0]
        const room = template.notes.find(
          (x) => x.type === BookingCommentType.CONTACT
        ).content
        const note = template.notes.find(
          (x) => x.type === BookingCommentType.PRIVATE
        ).content
        const purchaseOrderNumber = template.purchaseOrderNumber

        if (template) {
          this.formChanged =
            template.locationId !== bookingSnapshot.locationId ||
            template.startTimeLocation !== bookingSnapshot.startTime ||
            template.endTimeLocation !== bookingSnapshot.endTime ||
            template.classification !== bookingSnapshot.classificationId ||
            template.breakMinutes !== bookingSnapshot.minutesBreak ||
            Number(template.submittedByOrReplacing) !==
              Number(bookingSnapshot.submittedByOrReplacing) ||
            room !== bookingSnapshot.room ||
            template.reason !== bookingSnapshot.reason ||
            note !== bookingSnapshot.notes ||
            purchaseOrderNumber !== bookingSnapshot.purchaseOrderNumber ||
            !isEqual(
              [...template.certifications].sort(),
              [...bookingSnapshot.certifications].sort()
            ) ||
            !isEqual(
              [...template.grades].sort(),
              [...bookingSnapshot.grades].sort()
            ) ||
            !this.isEqualIgnoreOrder(
              template.confirmations,
              bookingSnapshot.confirmations,
              'contactId'
            ) ||
            !isEqual(
              [...this.dates].sort(),
              [...bookingSnapshot.dates].sort()
            ) ||
            !isEqual(
              Number(this.numberOfBookingsPerDate),
              Number(bookingSnapshot.numberOfBookingsPerDate)
            ) ||
            !isEqual(this.multipleDaysCheckbox, bookingSnapshot.multipleBooking)
        }
        this.$emit('editChanged', this.formChanged)
      } else {
        this.formChanged = true
      }
      this.$emit('updated')
    },
    handleBackClick() {
      if (this.editMode) {
        this.$emit('editChanged', this.formChanged)
      }
      if (this.formChanged) {
        this.$emit('updated')
      }
      this.$emit('backClicked')
    },
    isEqualIgnoreOrder(a, b, key = 'contactId') {
      if (!Array.isArray(a) || !Array.isArray(b)) return false

      const sortFn = (x, y) => {
        const xKey = x[key]
        const yKey = y[key]
        if (typeof xKey === 'string') return xKey.localeCompare(yKey)
        return xKey - yKey
      }

      const sortedA = [...a].sort(sortFn)
      const sortedB = [...b].sort(sortFn)

      return isEqual(sortedA, sortedB)
    },
    handleMultipleBookingCheckbox(value) {
      this.multipleDaysCheckbox = value
      if (this.editMode) {
        this.updated()
      }
    },
    validateDateSelected(dates) {
      if (!dates || dates.length === 0) return

      // In edit mode for a independent single-day booking, restrict the user to selecting only one date.
      if (
        this.editMode &&
        this.isSingleDayBooking &&
        this.booking?.groupId === null &&
        dates.length > 1
      ) {
        this.dates = [dates[dates.length - 1]] // keep only the latest selected date
      }

      const today = dayjs()
        .tz(this.timezone)
        .format('YYYY-MM-DD')

      if (
        dates.includes(today) &&
        this.hasPassedSameDayCutOff(this.timezone) &&
        !this.ignoreSameDayCutOff()
      ) {
        // Remove today's date from the selected list
        this.dates = this.dates.filter((date) => date !== today)

        this.showPastCurrentDayDialog = true
      }

      this.updated()
    },
    handleMultipleBookingsChange(value) {
      this.numberOfBookingsPerDate = value
      this.updated()
    },
    removeConfirmation(id) {
      this.payload.templates[0].confirmations = this.confirmations.filter(
        (x) => x.contactId !== id
      )
      this.updated()
    },
    toggleNotifyMe(value) {
      // If unchecked notify me, remove current contact
      if (!value) return this.removeConfirmation(this.currentUser.id)

      // If checked, add current contact to confirmations list
      const contact = this.contacts.find((x) => x.id === this.currentUser.id)
      const payload = {
        contactId: this.currentUser.id,
        sms: '',
        voice: '',
        email: false,
      }

      for (let index = 1; index <= this.numberOfPhoneNumberProps; ++index) {
        if (!contact[`phone${index}Type`]) continue

        // Set first available mobile number as SMS preference
        if (!payload.sms && contact[`phone${index}Type`] === PhoneType.MOBILE)
          payload.sms = `Phone${index}`

        // Set first available phone number as Voice preference
        if (!payload.voice) payload.voice = `Phone${index}`

        // If both are set, break out of loop
        if (payload.sms && payload.voice) break
      }

      payload.email = !!contact.emailAddress

      this.saveConfirmation(payload)
    },
    async changeLocation(bypass = false) {
      if (!this.formChanged || bypass) {
        this.$emit('changeLocation')
      } else {
        // Wait for users response to dialog
        const result = await this.$refs.unsavedChangesDialogForLocationChange.show(
          {
            text: this.$t('general.discardDialogBodyText'),
            title: this.$t('general.discardDialogTitle'),
            actions: {
              cancel: this.$t('general.cancel'),
              confirm: {
                text: this.$t('general.discardDialogDiscardBtnText'),
                color: 'blue',
              },
            },
            icon: false,
            persistant: true,
          }
        )

        if (result === 'ok') this.$emit('changeLocation')
      }
    },
    saveConfirmation(payload) {
      if (!payload || !payload.contactId) return null

      const found = this.confirmations.find(
        (x) => x.contactId === payload.contactId
      )

      if (found) {
        found.sms = payload.sms
        found.voice = payload.voice
        found.email = payload.email
      } else {
        const contact = this.contacts.find((x) => x.id === payload.contactId)
        const addConfirmation = {
          ...payload,
          ...{ fullName: contact.fullName },
        }
        this.payload.templates[0].confirmations.push(addConfirmation)
      }

      this.updated()
    },
    async submit() {
      if (!this.isValid) return
      this.clearErrors()

      const isEdit = this.editMode

      // Generate bookings based on dates selected multiplied by numberOfBookingsPerDate
      this.payload.bookings = []
      const bookingsPerDate = parseInt(this.numberOfBookingsPerDate)

      let areGroupedRequired = false

      if (this.dates.length > 1 || (isEdit && this.isBlockBooking)) {
        areGroupedRequired = true

        for (let i = 0; i < bookingsPerDate; i++) {
          this.payload.groups.push({
            id: isEdit && this.isBlockBooking ? this.booking.groupId : nanoid(), // For edit type block, we set group id here
            title: `Block ${i + 1}`,
            details: '',
          })
        }
      }

      for (const date of this.dates) {
        for (let i = 0; i < bookingsPerDate; i++) {
          this.payload.bookings.push({
            startDateLocation: date,
            templateId:
              isEdit && this.isSingleDayBooking
                ? this.booking.id
                : this.payload.templates[0].id, // For edit type single day, we set the booking id
            groupId: areGroupedRequired ? this.payload.groups[i].id : null,
          })
        }
      }

      // Handle edit-specific template ID adjustment
      if (isEdit && this.isSingleDayBooking) {
        this.payload.templates[0].id = this.booking.id
      }

      // Submit to API
      const response = await (isEdit
        ? this.editBooking(this.payload)
        : this.submitBooking(this.payload))

      if (response.isSuccess) {
        if (isEdit) {
          this.formChanged = false
          this.$emit('editChanged', this.formChanged)

          if (this.isSingleDayBooking && this.booking.groupId === null) {
            const newDate = this.dates?.[0]
            if (newDate) {
              const newUrl = `${this.$route.path}?${new URLSearchParams({
                ...this.$route.query,
                date: dayjs(newDate).format(DateFormatToken.YYYYMMDD),
              }).toString()}`
              window.history.replaceState({}, '', newUrl)
            }
          }
        }

        this.$emit('showSummaryPage', {
          response: response.data,
          payload: this.payload,
          bookingsPerDate,
          selectedLocation: this.selectedLocation,
          isClassificationHidden: this.isClassificationHidden,
        })
        return
      }

      // Handle validation errors
      this.error =
        typeof response.error === 'string'
          ? {
              message: this.$t('error.genericFailedRequestMessage'),
              code: 'unknown',
            }
          : response.error

      if (
        this.error.code ===
        'bookings_create_booking_creation_restricted_for_location'
      ) {
        // Clear dates if any land on a restriction
        this.dates = []

        // Reload restrictions to update UI
        await this.loadLocationBookingBlocks()
      }
    },
  },
}
</script>

<template>
  <section data-testid="booking-create-meta">
    <v-row class="justify-center">
      <v-col cols="12" xl="8">
        <BookingBreadcrumb
          :edit-mode="editMode"
          :sub-title="editMode && booking ? '#' + booking.id : ''"
          @backClicked="handleBackClick"
        />
      </v-col>
    </v-row>

    <v-row class="justify-center">
      <v-col cols="12" md="8">
        <BookingCreateRestrictedAlert
          v-if="hasBookingRestriction && !isLoadingBookingBlocks"
          :location-id="selectedLocation.locationId"
        />
        <ErrorAlert v-if="hasErrors" type="error" :error="error" />

        <v-alert
          v-if="hasExceededMaxAllowedBookings"
          id="booking-create-meta__exceeded-max-bookings-alert"
          type="error"
          text
        >
          {{
            $t('bookingCreate.alertTextExceededMaxBookings', {
              maxBookingsAllowed: maxNumBookingsPerDay,
            })
          }}
        </v-alert>
      </v-col>
    </v-row>
    <v-row v-if="showNoClassificationsError" class="justify-center">
      <v-card max-width="700" class="custom-elevation rounded-xl">
        <EmptyStateBlock
          data-testid="booking-create-meta-no-classifications"
          :title="
            $t('bookingCreate.classifications.error.noClassificationTitle')
          "
          :description="
            $t(
              'bookingCreate.classifications.error.noClassificationDescription',
              [$t('app.companyNameShort')]
            )
          "
        >
          <template v-slot:button>
            <div class="d-flex">
              <v-btn text outlined class="mr-2" @click="changeLocation(true)">{{
                $t('general.back')
              }}</v-btn>
            </div>
          </template>
        </EmptyStateBlock>
      </v-card>
    </v-row>
    <v-row v-else class="justify-center">
      <v-col cols="12" md="6" lg="4" xl="3">
        <v-date-picker
          v-model="dates"
          full-width
          multiple
          class="custom-elevation rounded-xl"
          :locale="getLocale"
          :min="minDate"
          :allowed-dates="allowedDates"
          :disabled="isDatePickerDisabled"
          @input="validateDateSelected"
        ></v-date-picker>

        <BookingConfirmationListSkeleton
          v-if="isLoadingBookingContacts"
          class="my-6"
        />
        <BookingConfirmationsList
          v-else
          class="my-6"
          :confirmations="confirmations"
          :contacts="contacts"
          @save="saveConfirmation"
          @remove="removeConfirmation"
        />
      </v-col>
      <v-col cols="12" md="6" lg="8" xl="5">
        <BookingCreateForm
          :selected-location="selectedLocation"
          :payload="payload"
          :hide-notify-me-checkbox="hideNotifyMeCheckbox"
          :edit-mode="editMode"
          :booking-mode="bookingMode"
          :booking="booking"
          :is-classification-hidden="isClassificationHidden"
          :mandatory-cert-ids="mandatoryCertIds"
          @timeUpdated="updateTime"
          @gradesUpdated="updateGrades"
          @certsUpdated="updateCerts"
          @classificationUpdated="updateClassification"
          @reasonUpdated="updateReason"
          @roomUpdated="updateRoom"
          @submittedByUpdated="updateSubmittedBy"
          @notesUpdated="updateNotes"
          @purchaseOrderNumberUpdated="updatePurchaseOrderNumber"
          @breaksUpdated="updateBreaks"
          @toggleNotifyMe="toggleNotifyMe"
          @changeLocation="changeLocation"
          @changeMultipleBookings="handleMultipleBookingsChange"
          @multipleDaysCheckbox="handleMultipleBookingCheckbox"
        />
        <v-spacer />

        <EditBanner v-if="editMode" :edit-description="editDescription" />
        <v-spacer />

        <DateAlertBanner
          v-if="showNoDatesAlert"
          :title="$t('bookingCreate.dates.error.noDateTitle')"
          :description="$t('bookingCreate.dates.error.noDateDescription')"
        />
        <v-spacer />

        <v-tooltip bottom :disabled="isValid">
          <template v-slot:activator="{ on }">
            <div
              id="booking-create-meta__submit-btn-container"
              class="d-inline-block mt-6"
              v-on="on"
            >
              <v-btn
                id="booking-create-meta__submit-btn"
                color="primary"
                class="custom-elevation"
                :disabled="editMode ? !formChanged || !isValid : !isValid"
                :loading="
                  isLoadingCRUD ||
                    isLoadingBookingBlocks ||
                    isLoadingEditBooking
                "
                x-large
                @click="submit"
              >
                <v-icon left>{{
                  editMode ? 'mdi-content-save-outline' : 'mdi-plus'
                }}</v-icon>
                {{
                  editMode
                    ? $t('bookingEdit.submitBtn')
                    : $t('bookingCreate.submitBtn')
                }}
              </v-btn>
            </div>
          </template>
          <span id="booking-create-meta__submit-btn-tooltip-text">
            {{ compSubmitButtonErrorTooltip }}
          </span>
        </v-tooltip>
      </v-col>
    </v-row>

    <v-dialog v-model="showPastCurrentDayDialog" max-width="600">
      <BookingPassedCutOffCard @closeModal="showPastCurrentDayDialog = false" />
    </v-dialog>

    <PromiseDialogOld ref="unsavedChangesDialogForLocationChange" />
  </section>
</template>
